import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Printer } from "lucide-react";
import { format } from "date-fns";
import { useRef } from "react";
import DOMPurify from "dompurify";

interface InvoiceViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: any;
}

export function InvoiceViewDialog({ open, onOpenChange, invoice }: InvoiceViewDialogProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (printRef.current) {
      const printContent = DOMPurify.sanitize(printRef.current.innerHTML);
      const printWindow = window.open('', '', 'height=800,width=800');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Invoice ${invoice.invoice_number}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 40px; }
                .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                .header h1 { color: #667eea; margin: 0; }
                .info-section { margin: 20px 0; }
                .info-section h3 { color: #333; border-bottom: 1px solid #ddd; padding-bottom: 10px; }
                .details { display: flex; justify-content: space-between; margin: 20px 0; }
                .details > div { flex: 1; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
                th { background-color: #f5f5f5; font-weight: bold; }
                .totals { text-align: right; margin-top: 20px; }
                .totals div { padding: 5px 0; }
                .total-amount { font-size: 20px; font-weight: bold; color: #667eea; }
                @media print {
                  button { display: none; }
                }
              </style>
            </head>
            <body>
              ${printContent}
              <script>
                window.onload = function() { window.print(); window.close(); }
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    }
  };

  const handleDownload = async () => {
    if (!printRef.current) return;
    
    // Create a complete HTML document for PDF generation
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 40px; 
              background: white;
              color: black;
            }
            .header { 
              text-align: center; 
              margin-bottom: 40px; 
              border-bottom: 2px solid #333; 
              padding-bottom: 20px; 
            }
            .header h1 { color: #667eea; margin: 0; font-size: 32px; margin-bottom: 10px; }
            .header p { color: #666; }
            .grid { display: flex; gap: 40px; margin-bottom: 40px; }
            .grid > div { flex: 1; }
            .section-title { 
              font-size: 16px;
              font-weight: bold; 
              margin-bottom: 15px; 
              border-bottom: 1px solid #ddd; 
              padding-bottom: 10px; 
            }
            .details { font-size: 14px; line-height: 1.8; }
            .details p { margin: 5px 0; }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 20px 0; 
            }
            th, td { 
              padding: 12px; 
              text-align: left; 
              border-bottom: 1px solid #ddd; 
            }
            th { 
              background-color: #f5f5f5; 
              font-weight: bold; 
            }
            .text-right { text-align: right; }
            .totals { 
              margin-top: 30px; 
              display: flex; 
              justify-content: flex-end; 
            }
            .totals-box { 
              width: 300px; 
            }
            .totals-row { 
              display: flex; 
              justify-content: space-between; 
              padding: 8px 0; 
              border-bottom: 1px solid #ddd; 
            }
            .totals-total { 
              display: flex; 
              justify-content: space-between; 
              padding: 15px 0; 
              border-top: 2px solid #333; 
              margin-top: 10px;
              font-size: 20px; 
              font-weight: bold; 
              color: #667eea; 
            }
            .payment-section, .terms-section { 
              margin-top: 30px; 
              padding-top: 20px; 
              border-top: 1px solid #ddd; 
            }
            .footer { 
              text-align: center; 
              margin-top: 50px; 
              padding-top: 20px; 
              border-top: 1px solid #ddd; 
              color: #666; 
              font-size: 12px; 
            }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          ${DOMPurify.sanitize(printRef.current.innerHTML)}
        </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice-${invoice.invoice_number}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Also trigger print dialog for PDF save option
    setTimeout(() => {
      const printWindow = window.open('', '', 'height=800,width=800');
      if (printWindow) {
        printWindow.document.write(invoiceHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    }, 100);
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Invoice {invoice.invoice_number}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button variant="default" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div ref={printRef} className="p-6 bg-white text-black">
          {/* Header */}
          <div className="text-center mb-8 border-b-2 border-gray-300 pb-6">
            <h1 className="text-3xl font-bold text-[#667eea] mb-2">INVOICE</h1>
            <p className="text-gray-600">Spotlight ADZ - AI-Powered Marketing Agency</p>
          </div>

          {/* Invoice Details & Client Info */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-lg mb-3 border-b border-gray-300 pb-2">Invoice Details</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Invoice Number:</strong> {invoice.invoice_number}</p>
                <p><strong>Issue Date:</strong> {format(new Date(invoice.issue_date), "MMMM dd, yyyy")}</p>
                <p><strong>Due Date:</strong> {format(new Date(invoice.due_date), "MMMM dd, yyyy")}</p>
                <p><strong>Status:</strong> <span className="capitalize">{invoice.status}</span></p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-3 border-b border-gray-300 pb-2">Bill To</h3>
              <div className="space-y-2 text-sm">
                <p className="font-semibold">{invoice.client_name}</p>
                <p>{invoice.client_email}</p>
                {invoice.client_address && (
                  <p className="whitespace-pre-line">{invoice.client_address}</p>
                )}
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          <div className="mb-8">
            <h3 className="font-semibold text-lg mb-3 border-b border-gray-300 pb-2">Items</h3>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left py-3 px-4">Description</th>
                  <th className="text-right py-3 px-4">Quantity</th>
                  <th className="text-right py-3 px-4">Unit Price</th>
                  <th className="text-right py-3 px-4">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.invoice_items?.map((item: any) => (
                  <tr key={item.id}>
                    <td className="py-3 px-4">{item.description}</td>
                    <td className="text-right py-3 px-4">{item.quantity}</td>
                    <td className="text-right py-3 px-4">{invoice.currency} {Number(item.unit_price).toFixed(2)}</td>
                    <td className="text-right py-3 px-4">{invoice.currency} {Number(item.amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64 space-y-2">
              <div className="flex justify-between py-2 border-b">
                <span>Subtotal:</span>
                <span>{invoice.currency} {Number(invoice.subtotal).toFixed(2)}</span>
              </div>
              {invoice.tax_rate > 0 && (
                <div className="flex justify-between py-2 border-b">
                  <span>Tax ({invoice.tax_rate}%):</span>
                  <span>{invoice.currency} {Number(invoice.tax_amount).toFixed(2)}</span>
                </div>
              )}
              {invoice.discount_amount > 0 && (
                <div className="flex justify-between py-2 border-b text-green-600">
                  <span>Discount:</span>
                  <span>-{invoice.currency} {Number(invoice.discount_amount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-t-2 border-gray-400 text-xl font-bold text-[#667eea]">
                <span>Total:</span>
                <span>{invoice.currency} {Number(invoice.total_amount).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          {invoice.payment_instructions && (
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-3 border-b border-gray-300 pb-2">Payment Instructions</h3>
              <p className="text-sm whitespace-pre-line">{invoice.payment_instructions}</p>
            </div>
          )}

          {/* Terms & Notes */}
          {(invoice.terms || invoice.notes) && (
            <div className="border-t border-gray-300 pt-6 space-y-4">
              {invoice.terms && (
                <div>
                  <h3 className="font-semibold mb-2">Terms & Conditions</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{invoice.terms}</p>
                </div>
              )}
              {invoice.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{invoice.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-sm text-gray-600 mt-8 pt-6 border-t border-gray-300">
            <p>Thank you for your business!</p>
            <p className="mt-2">© {new Date().getFullYear()} Spotlight ADZ. All rights reserved.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
